import { get as _get } from 'lodash';
import { fetchEditorialArticleById } from '../common/api/editorial';
import { redirectToNotFound } from './status';
import sanitizeArticleHTML from '../utils/sanitizeArticleHTML';
import getFeedGeotarget from '../utils/getFeedGeotarget';
import type { WavetrakStore, AppDispatch } from '../stores';
import type { Article, RegionContent } from '../types/editorial';

export const FETCH_EDITORIAL_ARTICLE = 'FETCH_EDITORIAL_ARTICLE';
export const FETCH_EDITORIAL_ARTICLE_SUCCESS = 'FETCH_EDITORIAL_ARTICLE_SUCCESS';
export const FETCH_EDITORIAL_ARTICLE_FAILURE = 'FETCH_EDITORIAL_ARTICLE_FAILURE';

/**
 * Maps a geotarget country code to the corresponding region content key
 */
const getRegionContentKey = (geotarget: string): 'auContent' | 'nzContent' | 'euContent' | null => {
  const upperGeotarget = geotarget.toUpperCase();
  if (upperGeotarget === 'AU') return 'auContent';
  if (upperGeotarget === 'NZ') return 'nzContent';
  if (upperGeotarget === 'EU') return 'euContent';
  return null;
};

/**
 * Resolves region-specific content into article.content based on user's geotarget
 */
const resolveRegionContent = (article: Article, geotarget: string): Article => {
  const regionKey = getRegionContentKey(geotarget);
  if (!regionKey || !article[regionKey]) {
    return article;
  }

  const regionContent: RegionContent = article[regionKey];
  const resolvedContent = { ...article.content };

  // Override title, subtitle, displayTitle only if they are non-null in the region content
  if (regionContent.title !== null) {
    resolvedContent.title = regionContent.title;
  }
  if (regionContent.subtitle !== null) {
    resolvedContent.subtitle = regionContent.subtitle;
  }
  if (regionContent.displayTitle !== null) {
    resolvedContent.displayTitle = regionContent.displayTitle;
  }

  return {
    ...article,
    content: resolvedContent,
  };
};

export const fetchEditorialArticle =
  (
    id: string | undefined,
    preview: boolean | null = null,
    cookies: any = null,
    countryCode: string | null = null,
    isServer = false,
  ) =>
  async (dispatch: AppDispatch, getState: WavetrakStore['getState']) => {
    dispatch({
      type: FETCH_EDITORIAL_ARTICLE,
    });

    try {
      const state = getState();
      const geotarget = countryCode || getFeedGeotarget(state);
      const fetchedArticle: Article = await fetchEditorialArticleById({
        id,
        preview,
        geotarget,
        cookies,
        isServer,
      });

      const resolvedArticle = resolveRegionContent(fetchedArticle, geotarget);

      const article = {
        ...resolvedArticle,
        premium: {
          ...resolvedArticle.premium,
          teaser: resolvedArticle.premium.teaser
            ? sanitizeArticleHTML(resolvedArticle.premium.teaser)
            : null,
        },
        content: {
          ...resolvedArticle.content,
          body: sanitizeArticleHTML(resolvedArticle.content.body),
        },
      };
      dispatch({
        type: FETCH_EDITORIAL_ARTICLE_SUCCESS,
        article,
      });
    } catch (error: unknown) {
      const err = error as { statusCode?: number; response?: { status?: number } };
      const wpStatusCode = _get(err, 'response.status', null);
      if (err.statusCode === 400 || wpStatusCode === 404) {
        dispatch(redirectToNotFound('The article was not found'));
      } else {
        dispatch({
          type: FETCH_EDITORIAL_ARTICLE_FAILURE,
          error,
        });
      }
    }
  };
